// Analytics management module for PPT Hero game
export default class AnalyticsManager {
  constructor(scene) {
    this.scene = scene;
  }

  // Calculate efficiency score for level completion
  calculateEfficiency() {
    if (!this.scene.analyticsData) return 0;
    
    const data = this.scene.analyticsData;
    const totalTime = data.levelCompletionTime - data.levelStartTime;
    const totalActions = data.shortcutAttempts + data.mouseClicks;
    
    if (totalActions === 0 || totalTime === 0) return 0;
    
    // Base score starts at 100
    let efficiency = 100;
    
    // Penalize for time taken (max 30 point penalty)
    const timePenalty = Math.min(30, (totalTime / 60) * 10); // 10 points per minute
    efficiency -= timePenalty;
    
    // Penalize for errors (max 40 point penalty)
    const errorPenalty = Math.min(40, (data.errors.misstypedShortcuts + data.errors.incorrectSequences + data.errors.missclicks) * 5);
    efficiency -= errorPenalty;
    
    // Bonus for shortcut usage (max 20 point bonus)
    const shortcutBonus = Math.min(20, Object.keys(data.shortcutsUsed).length * 2);
    efficiency += shortcutBonus;
    
    // Ensure score is between 0 and 100
    return Math.max(0, Math.min(100, Math.round(efficiency)));
  }

  // Track keystrokes for analytics
  trackKeystrokes() {
    if (!this.scene.analyticsData) {
      this.scene.analyticsData = {
        levelStartTime: Date.now() / 1000,
        levelCompletionTime: 0,
        shortcutAttempts: 0,
        successfulShortcuts: 0,
        mouseClicks: 0,
        keystrokeCount: 0,
        errors: {
          misstypedShortcuts: 0,
          incorrectSequences: 0,
          missclicks: 0
        },
        shortcutsUsed: {},
        shortcutsMissed: {}
      };
    }
    
    this.scene.analyticsData.keystrokeCount++;
  }

  // Save level analytics data
  saveLevelAnalytics() {
    if (!this.scene.analyticsData) return;
    
    try {
      // Calculate final metrics
      const data = this.scene.analyticsData;
      data.levelCompletionTime = Date.now() / 1000;
      data.efficiency = this.calculateEfficiency();
      
      // Save to localStorage
      const savedAnalytics = JSON.parse(localStorage.getItem('pptHeroAnalytics') || '{}');
      const levelKey = `level_${this.scene.currentLevel}_${this.scene.gameMode}`;
      savedAnalytics[levelKey] = data;
      localStorage.setItem('pptHeroAnalytics', JSON.stringify(savedAnalytics));
      
      console.log('📊 Saved level analytics:', data);
      
      // Reset for next level
      this.scene.analyticsData = null;
      
    } catch (error) {
      console.error('Error saving level analytics:', error);
    }
  }

  // Track shortcut usage
  trackShortcutUsed(shortcutKey, description = '') {
    if (!this.scene.analyticsData) return;
    
    if (!this.scene.analyticsData.shortcutsUsed[shortcutKey]) {
      this.scene.analyticsData.shortcutsUsed[shortcutKey] = 0;
    }
    this.scene.analyticsData.shortcutsUsed[shortcutKey]++;
    
    this.scene.analyticsData.successfulShortcuts++;
    console.log(`📊 Shortcut tracked: ${shortcutKey} = ${this.scene.analyticsData.shortcutsUsed[shortcutKey]}`);
  }

  // Track shortcut attempts
  trackShortcutAttempt() {
    if (!this.scene.analyticsData) return;
    this.scene.analyticsData.shortcutAttempts++;
  }

  // Track errors
  trackError(errorType) {
    if (!this.scene.analyticsData || !this.scene.analyticsData.errors[errorType]) return;
    this.scene.analyticsData.errors[errorType]++;
  }

  // Get analytics summary
  getAnalyticsSummary() {
    try {
      const savedAnalytics = JSON.parse(localStorage.getItem('pptHeroAnalytics') || '{}');
      const summary = {
        totalLevels: Object.keys(savedAnalytics).length,
        averageEfficiency: 0,
        totalShortcuts: 0,
        totalErrors: 0,
        levels: savedAnalytics
      };
      
      let totalEfficiency = 0;
      let totalShortcuts = 0;
      let totalErrors = 0;
      
      Object.values(savedAnalytics).forEach(level => {
        if (level.efficiency) totalEfficiency += level.efficiency;
        if (level.successfulShortcuts) totalShortcuts += level.successfulShortcuts;
        if (level.errors) {
          totalErrors += (level.errors.misstypedShortcuts + level.errors.incorrectSequences + level.errors.missclicks);
        }
      });
      
      if (summary.totalLevels > 0) {
        summary.averageEfficiency = Math.round(totalEfficiency / summary.totalLevels);
      }
      summary.totalShortcuts = totalShortcuts;
      summary.totalErrors = totalErrors;
      
      return summary;
    } catch (error) {
      console.error('Error getting analytics summary:', error);
      return null;
    }
  }

  // Clear all analytics
  clearAnalytics() {
    try {
      localStorage.removeItem('pptHeroAnalytics');
      console.log('📊 Cleared all analytics data');
    } catch (error) {
      console.error('Error clearing analytics:', error);
    }
  }

  // Export analytics data
  exportAnalytics() {
    return {
      analytics: this.getAnalyticsSummary(),
      exportDate: new Date().toISOString()
    };
  }
}

